import { ReactNode } from 'react';
import LoadingSpinner from './LoadingSpinner';

/**
 * Represents an authenticated user in the application.
 *
 * @property id - The unique identifier for the user.
 * @property role - The role assigned to the user (e.g., 'admin', 'user').
 * @property email - The user's email address.
 */
interface AuthUser {
  id: string;
  role: string;
  email: string;
}

/**
 * Represents the authentication context for the application.
 *
 * @property user - The currently authenticated user, or `null` if not authenticated.
 * @property isAuthenticated - Indicates whether a user is authenticated.
 * @property loading - (Optional) Indicates whether authentication status is being determined.
 */
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading?: boolean;
}

/**
 * Props for the `AuthGuard` component, which controls access to its children based on authentication and authorization.
 *
 * @property {ReactNode} children - The content to render if access is granted.
 * @property {string[]} [requireRole] - Optional array of roles required to access the children. If not provided, any authenticated user is allowed.
 * @property {ReactNode} [fallback] - Optional fallback content to display while authentication status is being determined.
 * @property {AuthContextType} authContext - The authentication context containing user and auth state information.
 * @property {ReactNode} [unauthorizedComponent] - Optional component to render if the user is authenticated but lacks the required role(s).
 */
interface AuthGuardProps {
  children: ReactNode;
  requireRole?: string[];
  fallback?: ReactNode;
  authContext: AuthContextType;
  unauthorizedComponent?: ReactNode;
}

/**
 * A React component that protects its children by enforcing authentication and optional role-based access control.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The content to render if access is granted.
 * @param {string[]} [props.requireRole=[]] - An array of roles required to access the children. If empty, any authenticated user is allowed.
 * @param {React.ReactNode} [props.fallback] - An optional component to render if the user is not authenticated.
 * @param {object} props.authContext - The authentication context containing user, isAuthenticated, and loading states.
 * @param {object} [props.authContext.user] - The authenticated user object, expected to have a `role` property.
 * @param {boolean} props.authContext.isAuthenticated - Whether the user is authenticated.
 * @param {boolean} props.authContext.loading - Whether authentication status is still loading.
 * @param {React.ReactNode} [props.unauthorizedComponent] - An optional component to render if the user is authenticated but lacks the required role.
 *
 * @returns {React.ReactNode} The rendered children if access is granted, otherwise a fallback, loading spinner, or unauthorized message/component.
 *
 * @example
 * <AuthGuard
 *   requireRole={['admin']}
 *   fallback={<LoginPrompt />}
 *   authContext={auth}
 *   unauthorizedComponent={<CustomUnauthorized />}
 * >
 *   <AdminDashboard />
 * </AuthGuard>
 */
export default function AuthGuard({ 
  children, 
  requireRole = [], 
  fallback,
  authContext,
  unauthorizedComponent
}: AuthGuardProps) {
  const { user, isAuthenticated, loading } = authContext;

  // Show loading state while authentication is being determined
  if (loading) {
    return <LoadingSpinner fullScreen message="Authenticating..." />;
  }

  // Show fallback or default message for unauthenticated users
  if (!isAuthenticated) {
    return fallback || (
      <LoadingSpinner fullScreen message="Please log in to continue..." />
    );
  }

  // Check role requirements
  if (requireRole.length > 0 && user?.role && !requireRole.includes(user.role)) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center py-12">
          <div className="h-16 w-16 text-red-500 mx-auto mb-4">
            <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500">
            Required: {requireRole.join(', ')} | Your role: {user?.role || 'Unknown'}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
